import { Link } from "react-router-dom";
import { useState } from "react";
import { LazyImage } from "@/components/LazyImage";
import { ArrowUpRight } from "lucide-react";
import SectionHeader from "@/components/SectionHeader";
import { useHomeWallSettings, buildWallGridClasses, wallDefaults } from "@/hooks/useHomeWallSettings";

interface DifesaBrand {
  name: string;
  category: string;
  slug: string;
  logo: string;
  website: string;
  hidden?: boolean;
}

interface BrandWallDifesaProps {
  id?: string;
  eyebrow?: string;
  title: string;
  description: string;
  brands: DifesaBrand[];
}

/**
 * Wall Marchi della Difesa — coerente con BrandWall ma senza hero background.
 * Hover: logo si ingrandisce leggermente, eyebrow "Difesa" appare in alto a sinistra,
 * freccia ArrowUpRight appare in basso a destra.
 */
const DifesaItem = ({ brand }: { brand: DifesaBrand }) => {
  const [error, setError] = useState(false);
  const isInternal = brand.website.startsWith("/");

  const inner = (
    <div className="group relative aspect-square w-full overflow-hidden border border-border bg-card transition-all duration-500 hover:border-terracotta hover:scale-[1.01] hover:z-10">
      {/* Logo — sempre visibile, leggero scale all'hover */}
      <div className="absolute inset-4 sm:inset-5 flex items-center justify-center transition-transform duration-500 group-hover:scale-[1.06]">
        {brand.logo && !error ? (
          <LazyImage
            src={brand.logo}
            alt={`${brand.name} logo`}
            className="w-full h-full"
            width={240}
            height={240}
            loading="lazy"
            fit="contain"
            onError={() => setError(true)}
          />
        ) : (
          <span className="font-display text-2xl text-foreground/70 text-center">
            {brand.name}
          </span>
        )}
      </div>

      {/* Reveal TOP — eyebrow "Difesa" in alto a sinistra */}
      <div className="absolute inset-x-0 top-0 px-3.5 sm:px-4 pt-3 opacity-0 -translate-y-2 transition-all duration-500 group-hover:opacity-100 group-hover:translate-y-0">
        <p className="text-[10px] sm:text-[11px] uppercase tracking-[0.22em] text-terracotta">
          Difesa
        </p>
      </div>

      {/* Reveal BOTTOM — freccia in basso a destra */}
      <div className="absolute inset-x-0 bottom-0 flex items-end justify-end px-3.5 sm:px-4 pb-3 sm:pb-3.5 opacity-0 translate-y-2 transition-all duration-500 group-hover:opacity-100 group-hover:translate-y-0">
        <ArrowUpRight className="h-5 w-5 text-terracotta shrink-0 transition-transform duration-500 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
      </div>
    </div>
  );

  return isInternal ? (
    <Link to={brand.website} aria-label={brand.name} className="block">
      {inner}
    </Link>
  ) : (
    <a href={brand.website} target="_blank" rel="noopener noreferrer" aria-label={brand.name} className="block">
      {inner}
    </a>
  );
};

const BrandWallDifesa = ({ id, eyebrow, title, description, brands }: BrandWallDifesaProps) => {
  const { data: settings } = useHomeWallSettings("marchi-difesa");
  const s = settings || wallDefaults["marchi-difesa"];
  if (!s.is_visible) return null;

  let visible = brands.filter((b) => !b.hidden);
  if (s.max_items && s.max_items > 0) visible = visible.slice(0, s.max_items);

  return (
    <section id={id} className="py-5 md:py-7 border-t border-border bg-secondary/30 scroll-mt-20">
      <div className="container mx-auto px-6">
        <SectionHeader eyebrow={eyebrow} title={title} description={description} />
        <div className={buildWallGridClasses(s)}>
          {visible.map((b) => (
            <DifesaItem key={b.slug} brand={b} />
          ))}
        </div>
      </div>
    </section>
  );
};

export default BrandWallDifesa;
